import React from 'react';
import { MoreHorizontalUniversal, SwapUniversal } from '@duxton-web/icons';
import {
  Button,
  Divider,
  Popover,
  Space,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import { AccessControlWrapper } from '@/common/components';
import { VariableState } from '@/constants';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { useAppContext } from '@/contexts/app-context';
import { useVariable } from '@/contexts/VariableContext';
import { MatchInfo } from '@/pages/RolloutTab/RolloutEditor/SearchRolloutBy';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';

interface RolloutTargetsHeaderProps extends CreateRolloutButtonProps {
  disabled: boolean;
  expandRolloutTargets: boolean;
  enableReorder: boolean;
  matchInfo: MatchInfo;
  showSearchByContraints: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void;
  toggleRolloutTargetsPanel: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void;
  toggleEnableReorder: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

interface ActionsProps extends RolloutTargetsHeaderProps {}

interface ReorderButtonProps {
  matchInfo: MatchInfo;
  enableReorder: boolean;
  toggleEnableReorder: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

interface CreateRolloutButtonProps {
  handleNewTarget: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

const ReorderButton = ({
  matchInfo,
  enableReorder,
  toggleEnableReorder,
}: ReorderButtonProps) => {
  return (
    <Button
      type="link"
      size="small"
      onClick={toggleEnableReorder}
      icon={<SwapUniversal />}
      disabled={matchInfo.matchRolloutTargetsCount === 0}
    >
      {!enableReorder ? 'Reorder targets' : 'Finish reordering'}
    </Button>
  );
};

const CreateRolloutButton = ({ handleNewTarget }: CreateRolloutButtonProps) => {
  const variable = useVariable();
  const appContext = useAppContext();
  const { environment } = appContext;

  const managedByPlatform = variable?.metadata?.managedByPlatform;
  const isManagedByPlatform = Boolean(managedByPlatform);

  const renderButton = (hasAccess: boolean) => {
    const isDisabled =
      !hasAccess ||
      variable?.state === VariableState.ARCHIVED ||
      isManagedByPlatform;

    const button = (
      <Button
        size="small"
        type="primary"
        disabled={isDisabled}
        onClick={(e) => {
          e.stopPropagation();
          handleNewTarget(e);
        }}
      >
        {`Create Rollout in ${
          environment === SystemEnvironment.STAGING ? 'Staging' : 'Production'
        }`}
      </Button>
    );

    if (isManagedByPlatform) {
      return (
        <Tooltip title={`Rollout is managed by ${managedByPlatform}`}>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <AccessControlWrapper
      action={UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
      techFamilies={[variable.techFamily]}
      accessTargets={[buildAccessTargetFromVariable(variable)]}
      showTooltip={false}
    >
      {renderButton}
    </AccessControlWrapper>
  );
};

const Actions = ({
  disabled,
  expandRolloutTargets,
  matchInfo,
  showSearchByContraints,
  toggleRolloutTargetsPanel,
  enableReorder,
  toggleEnableReorder,
  ...createRolloutButtonProps
}: ActionsProps) => {
  if (disabled) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Space.Compact>
        <Button
          type="secondary"
          size="small"
          onClick={toggleRolloutTargetsPanel}
          disabled={matchInfo.matchRolloutTargetsCount === 0}
        >
          {!expandRolloutTargets ? `Expand All` : `Collapse All`}
        </Button>
        <Popover
          content={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button size="small" type="link" onClick={showSearchByContraints}>
                View Rollouts By Product
              </Button>
              <ReorderButton
                matchInfo={matchInfo}
                enableReorder={enableReorder}
                toggleEnableReorder={toggleEnableReorder}
              />
            </div>
          }
        >
          <Button
            type="secondary"
            size="small"
            shape="circle"
            icon={<MoreHorizontalUniversal />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      </Space.Compact>

      <CreateRolloutButton {...createRolloutButtonProps} />

      <Divider type="vertical" />
    </div>
  );
};

export const RolloutTargetsHeader = (props: RolloutTargetsHeaderProps) => {
  const appContext = useAppContext();
  const { environment } = appContext;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
        }}
      >
        <Typography.Text
          strong
        >{`Rollouts ${environment === SystemEnvironment.STAGING ? 'Staging' : 'Production'}`}</Typography.Text>
        <Typography.Metadata style={{ color: '#A3A3A3' }}>
          Add rollout targets and list them in the order you want them
          evaluated.
        </Typography.Metadata>
      </div>

      <Actions {...props} />
    </div>
  );
};
